import type { CustomAgentTriggerKind } from '@axilon-apps/domains-ontology-ui-state'

import { formatDurationMs } from '../../lib/format-duration'

export { formatDurationMs }

const SECOND = 1000

/**
 * Elapsed time from `started_at` to `finished_at`, or to `now` when the run
 * is still open. Missing start or clock skew (end before start) renders as
 * an em dash rather than a negative duration.
 */
export function formatRunDuration(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
  now = Date.now()
): string {
  if (!startedAt) return '—'
  const start = Date.parse(startedAt)
  if (!Number.isFinite(start)) return '—'
  const end = finishedAt ? Date.parse(finishedAt) : now
  if (!Number.isFinite(end)) return '—'
  return formatDurationMs(end - start)
}

/**
 * Time from the previous recorded instant to this invocation's `created_at`.
 * Invocations are insert-only after the tool returns, so `updated_at` equals
 * `created_at` and is not a duration. The gap between consecutive posts is
 * the only timing the row carries.
 */
export function invocationStepMs(
  createdAt: string | null | undefined,
  previousAt: string | null | undefined
): number | null {
  if (!createdAt || !previousAt) return null
  const end = Date.parse(createdAt)
  const start = Date.parse(previousAt)
  if (!Number.isFinite(end) || !Number.isFinite(start)) return null
  const ms = end - start
  return ms <= 0 ? null : ms
}

/** True when both timestamps parse and fall in the same UTC second. */
export function sameSecond(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  const ta = Date.parse(a)
  const tb = Date.parse(b)
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return false
  return Math.floor(ta / SECOND) === Math.floor(tb / SECOND)
}

export function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

export function formatCompactTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export function describeTriggerKind(kind: CustomAgentTriggerKind | string): string {
  switch (kind) {
    case 'cron':
      return 'Scheduled'
    case 'condition':
      return 'Trigger fired'
    case 'manual':
      return 'Manual'
    case 'invoke':
      return 'Invoked'
    case 'follow_up':
      return 'Follow-up'
    case 'chat':
      return 'From chat'
    default:
      return 'Run'
  }
}

export function shortRunId(id: string): string {
  return id.slice(0, 8)
}

export interface RunErrorPart {
  text: string
  mono: boolean
}

const ERROR_TOKEN =
  /(\[[^\]]+\]\S+|\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b|\b\d{3}\s+[A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+)*)/g

/** Split an error string so identifiers (UUIDs, tag paths, HTTP statuses) can render in mono. */
export function splitRunError(error: string): RunErrorPart[] {
  const parts: RunErrorPart[] = []
  let last = 0
  for (const match of error.matchAll(ERROR_TOKEN)) {
    const index = match.index ?? 0
    if (index > last) parts.push({ text: error.slice(last, index), mono: false })
    parts.push({ text: match[0], mono: true })
    last = index + match[0].length
  }
  if (last < error.length) parts.push({ text: error.slice(last), mono: false })
  return parts.length > 0 ? parts : [{ text: error, mono: false }]
}
